import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRight,
  MessageCircle,
  Headphones,
  ClipboardCheck,
  Users,
  Sparkles,
  Mail,
  GraduationCap,
} from 'lucide-react';

const PILLARS = [
  {
    icon: ClipboardCheck,
    title: 'Review with Care',
    body: 'Receive written and recorded submissions from learners. Give detailed, kind, structured feedback — at your own pace.',
  },
  {
    icon: Headphones,
    title: 'Listen Before You Correct',
    body: 'Each submission is a voice. Read it, hear it, then respond. Selora is built around teachers who guide more than they grade.',
  },
  {
    icon: MessageCircle,
    title: 'Conversational Feedback',
    body: 'Write in the comment box, attach a voice note, or schedule a follow-up. No rigid rubrics. No noisy dashboards.',
  },
  {
    icon: Sparkles,
    title: 'Pedagogy First',
    body: 'No gamification. No badges. Just a thoughtful, CEFR-aligned environment that respects how adults really learn a language.',
  },
];

const HOW_IT_WORKS = [
  {
    n: '01',
    title: 'You receive a submission',
    body: 'A learner shares a recording, a paragraph, or a guided dialogue from the Speaking Lab.',
  },
  {
    n: '02',
    title: 'You review at your pace',
    body: 'Open the submission inside your private dashboard. Annotate. Listen again. Reflect.',
  },
  {
    n: '03',
    title: 'You answer with intention',
    body: 'Send one clear, constructive reply — the kind of feedback you would want from a mentor.',
  },
];

export default function TeachersPortal() {
  const { user } = useAuth();
  const isTeacher = user?.role === 'teacher';

  return (
    <div className="bg-[#FAF7F0] min-h-screen" data-testid="teachers-portal-page">
      {/* Hero */}
      <section className="pt-20 pb-24 relative overflow-hidden">
        {/* Decorative gold underline */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="max-w-[900px] mx-auto px-8 text-center">
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-3 mb-6" data-testid="hero-eyebrow">
            <div className="h-px w-12 bg-[#D4AF37]/60" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-[#6B4E37] font-semibold">
              For Selora Teachers
            </span>
            <div className="h-px w-12 bg-[#D4AF37]/60" />
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2F4A] leading-[1.05] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="hero-title"
          >
            The Teachers&nbsp;Portal
          </h1>

          <p
            className="text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto mb-10"
            style={{ fontFamily: "'Inter', sans-serif" }}
            data-testid="hero-tagline"
          >
            A quiet, focused space where Selora teachers review learner submissions,
            shape oral progress, and give the kind of feedback that turns hesitation
            into fluency.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            {isTeacher ? (
              <Link
                to="/teacher"
                className="bg-[#1A2F4A] hover:bg-[#243a58] text-white px-10 py-4 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2.5 group"
                data-testid="cta-go-to-dashboard">
                Open my dashboard
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : (
              <>
                <Link
                  to="/pricing"
                  className="bg-[#1A2F4A] hover:bg-[#243a58] text-white px-10 py-4 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2.5 group"
                  data-testid="cta-book-session">
                  Book a session
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/login"
                  className="bg-white border border-[#1A2F4A] text-[#1A2F4A] hover:bg-[#FAF7F0] px-8 py-4 rounded-full text-base font-semibold transition-all flex items-center gap-2"
                  data-testid="cta-teacher-login">
                  Sign in as a teacher
                </Link>
              </>
            )}
          </div>

          {/* Trust line */}
          <p className="text-[12px] tracking-wide text-[#6B4E37]/70 mt-8" data-testid="hero-trust">
            Built for educators who teach French as a craft — not as a checklist.
          </p>
        </div>
      </section>

      {/* Mission strip */}
      <section className="bg-[#1A2F4A] py-14 text-center" data-testid="mission-strip">
        <div className="max-w-[760px] mx-auto px-8">
          <p
            className="text-xl md:text-2xl text-white/90 leading-relaxed italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            &ldquo;Every submission is a learner stepping out of silence.
            Selora teachers are the voices who answer them.&rdquo;
          </p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="h-px w-10 bg-[#D4AF37]/60" />
            <span className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase">Selora</span>
            <div className="h-px w-10 bg-[#D4AF37]/60" />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 bg-[#FAF7F0]" data-testid="pillars-section">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="text-center mb-14">
            <h2
              className="text-3xl md:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What you do inside the portal
            </h2>
            <p className="text-sm text-[#6B4E37] max-w-xl mx-auto leading-relaxed">
              A clean, modern workspace for thoughtful correction and human guidance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="bg-white border border-[#E8E4DC] rounded-2xl p-8 hover:border-[#D4AF37]/60 hover:shadow-md transition-all"
                  data-testid={`pillar-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#1A2F4A]/5 flex items-center justify-center mb-5 border border-[#D4AF37]/40">
                    <Icon className="w-5 h-5 text-[#1A2F4A]" />
                  </div>
                  <h3
                    className="text-xl font-bold text-[#1A2F4A] mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white border-t border-[#E8E4DC]" data-testid="how-it-works-section">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="text-center mb-14">
            <h2
              className="text-3xl md:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How a Selora review unfolds
            </h2>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-16 bg-[#D4AF37]/60" />
              <span className="text-[#D4AF37]">⚜</span>
              <div className="h-px w-16 bg-[#D4AF37]/60" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={i}
                className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-2xl p-7 relative"
                data-testid={`step-${i}`}
              >
                <span
                  className="absolute -top-4 left-7 bg-[#1A2F4A] text-[#D4AF37] text-xs font-bold tracking-[0.3em] px-3 py-1 rounded-full"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.n}
                </span>
                <h3
                  className="text-lg font-bold text-[#1A2F4A] mt-3 mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.title}
                </h3>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Become a teacher CTA */}
      <section className="py-20 bg-[#FAF7F0]" data-testid="become-teacher-section">
        <div className="max-w-[820px] mx-auto px-8">
          <div className="bg-white border border-[#D4AF37]/30 rounded-3xl p-10 md:p-14 text-center shadow-sm">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1A2F4A] mb-6">
              <GraduationCap className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <h2
              className="text-3xl font-bold text-[#1A2F4A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Want to teach with Selora?
            </h2>
            <p className="text-sm md:text-base text-[#4A4A4A] leading-relaxed max-w-xl mx-auto mb-8">
              We work with a small group of French educators who care about depth,
              elegance and oral confidence. If that sounds like you, we&apos;d love
              to talk.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Link
                to="/contact"
                className="bg-[#1A2F4A] hover:bg-[#243a58] text-white px-10 py-4 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2.5 group"
                data-testid="cta-apply-teacher"
              >
                <Mail className="w-4 h-4" />
                Get in touch
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/club-francophone"
                className="text-[#1A2F4A] hover:text-[#D4AF37] text-sm font-semibold inline-flex items-center gap-2 transition-colors"
                data-testid="cta-discover-club"
              >
                <Users className="w-4 h-4" />
                Discover Le Club Francophone
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
